import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FloppyDisk, GitBranch } from "@phosphor-icons/react";
import { useDocumentStore } from "../../stores/document";
import { api } from "../../lib/api";
import type { VersionListItem, DocumentVersion } from "../../types/document";
import {
  VersionTimeline,
  formatVersionTooltip,
  formatVersionShortLabel,
} from "../../components/version/VersionTimeline";
import { DiffViewer } from "../../components/version/DiffViewer";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "../../components/ui/Dialog";

export function VersionPanel() {
  const currentDocument = useDocumentStore((s) => s.currentDocument);
  const saveDocument = useDocumentStore((s) => s.saveDocument);

  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedVersionContent, setSelectedVersionContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snapshotName, setSnapshotName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const fetchVersions = useCallback(async () => {
    if (!currentDocument?.id) return;
    setIsLoading(true);
    try {
      const list = await api.get<VersionListItem[]>(
        `/api/v1/documents/${currentDocument.id}/versions`
      );
      setVersions(list);
    } catch {
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentDocument?.id]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleSelectVersion = useCallback(async (versionId: string) => {
    setSelectedVersionId(versionId);
    try {
      const version = await api.get<DocumentVersion>(`/api/v1/versions/${versionId}`);
      setSelectedVersionContent(version.content);
    } catch {
      setSelectedVersionContent(null);
    }
  }, []);

  const handleCreateSnapshot = useCallback(async () => {
    if (!currentDocument?.id) return;
    setIsCreating(true);
    try {
      await saveDocument();
      await api.post("/api/v1/versions", {
        document_id: currentDocument.id,
        snapshot_name: snapshotName.trim() || null,
      });
      setCreateDialogOpen(false);
      setSnapshotName("");
      await fetchVersions();
    } catch {
      // Snapshot creation failed -- dialog stays open so user can retry
    } finally {
      setIsCreating(false);
    }
  }, [currentDocument?.id, snapshotName, saveDocument, fetchVersions]);

  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    setSnapshotName("");
  }, []);

  const selectedTimelineVersion = useMemo(() => {
    if (!selectedVersionId) return null;
    const v = versions.find((x) => x.id === selectedVersionId);
    if (!v) return null;
    return {
      id: v.id,
      snapshot_name: v.snapshot_name,
      created_at: v.created_at,
    };
  }, [selectedVersionId, versions]);

  if (!currentDocument) {
    return (
      <div className="flex flex-col gap-2 px-3 pb-3 pt-2">
        <p className="text-[13px] text-[var(--text-muted)]">
          Open a document to manage versions
        </p>
      </div>
    );
  }

  const timelineVersions = versions.map((v) => ({
    id: v.id,
    snapshot_name: v.snapshot_name,
    created_at: v.created_at,
  }));

  return (
    <div className="flex flex-col gap-3 px-3 pb-4 pt-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
          <GitBranch size={18} weight="duotone" className="shrink-0 text-[var(--color-lever-orange)]" />
          <span className="truncate">Version history</span>
        </h3>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<FloppyDisk size={14} weight="fill" />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Snapshot
        </Button>
      </div>

      <VersionTimeline
        versions={timelineVersions}
        selectedVersionId={selectedVersionId}
        onSelect={handleSelectVersion}
      />

      {isLoading ? (
        <p className="text-[11px] text-[var(--text-muted)]">Loading versions…</p>
      ) : (
        <AnimatePresence mode="wait">
          {selectedVersionId && selectedVersionContent !== null ? (
            <motion.div
              key={selectedVersionId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <p className="mb-2 text-[11px] leading-snug text-[var(--text-muted)]">
                <span className="font-medium text-[var(--text-secondary)]">Diff vs current</span>
                {selectedTimelineVersion && (
                  <>
                    {" · "}
                    <span
                      className="text-[var(--text-muted)]"
                      title={formatVersionTooltip(selectedTimelineVersion)}
                    >
                      {formatVersionShortLabel(selectedTimelineVersion)}
                    </span>
                  </>
                )}
              </p>
              <DiffViewer
                embedded
                oldContent={selectedVersionContent}
                newContent={currentDocument.content}
              />
            </motion.div>
          ) : selectedVersionId ? (
            <p className="text-[11px] text-[var(--text-muted)]">Loading version…</p>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[11px] text-[var(--text-muted)]"
            >
              Select a snapshot on the timeline to compare it to the current editor
            </motion.p>
          )}
        </AnimatePresence>
      )}

      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogHeader>Create Snapshot</DialogHeader>
        <DialogBody>
          <p className="mb-3 text-sm text-[var(--text-secondary)]">
            Save the current document state as a named snapshot. You can leave the
            name empty for an auto-saved timestamp.
          </p>
          <Input
            placeholder="Snapshot name (optional)"
            value={snapshotName}
            onChange={(e) => setSnapshotName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateSnapshot();
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={handleCloseCreateDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSnapshot}
            loading={isCreating}
            leftIcon={<FloppyDisk size={14} weight="fill" />}
          >
            Create
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
